import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { Checkbox as CheckboxPrimitive } from 'radix-ui';
import { Check } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface CheckboxProps {
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
  /**
   * `square` für Auswahl in Formularen und Tabellen, `circle` für erledigte
   * Aufgaben (auf Touch-Geräten mit 44 px Trefferfläche).
   */
  shape?: 'square' | 'circle';
  /** Sichtbares, klickbares Label. */
  label?: ReactNode;
  /** Ohne sichtbares Label Pflicht. */
  ariaLabel?: string;
  disabled?: boolean;
  id?: string;
  name?: string;
  className?: string;
}

/** Eckig für Auswahl, rund für Aufgaben. */
export function Checkbox({ checked, defaultChecked, onChange, shape = 'square', label, ariaLabel, disabled, id, name, className }: CheckboxProps) {
  const autoId = useId();
  const boxId = id ?? autoId;
  const box = (
    <CheckboxPrimitive.Root
      id={boxId}
      name={name}
      checked={checked}
      defaultChecked={defaultChecked}
      onCheckedChange={(v) => onChange?.(v === true)}
      aria-label={ariaLabel}
      disabled={disabled}
      className={cn(
        'inline-flex shrink-0 cursor-pointer items-center justify-center border-[1.5px] border-input bg-transparent text-selection-foreground transition-colors duration-[120ms]',
        'data-[state=checked]:border-selection data-[state=checked]:bg-selection disabled:cursor-not-allowed disabled:opacity-45',
        // Rund (Aufgaben): Auf Touch-Geräten reicht die Trefferfläche 10 px über
        // den Kreis hinaus, 24 + 20 = 44 px, die Optik bleibt gleich. Gemessen
        // ab dem Innenrand (1,5 px Rand), darum 11,5 px.
        shape === 'circle'
          ? 'relative size-5 rounded-full touch:size-6 touch:after:absolute touch:after:-inset-[11.5px] touch:after:rounded-full'
          : 'size-[18px] rounded-xs touch:size-6',
        !label && className,
      )}
    >
      <CheckboxPrimitive.Indicator>
        <Check size={shape === 'circle' ? 13 : 12} strokeWidth={3} aria-hidden />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
  if (!label) return box;
  return (
    <span className={cn('inline-flex items-center gap-2', disabled && 'opacity-45', className)}>
      {box}
      <label htmlFor={boxId} className={cn('cursor-pointer', disabled && 'cursor-not-allowed')}>
        {label}
      </label>
    </span>
  );
}

export interface NativeCheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode;
}

/**
 * Natives <input type="checkbox"> mit ref, für react-hook-form (`register`).
 * Haken in selection, eckig wie Checkbox. (Ergänzung, nicht im Design System.)
 */
export const NativeCheckbox = forwardRef<HTMLInputElement, NativeCheckboxProps>(function NativeCheckbox(
  { label, className, ...rest },
  ref,
) {
  return (
    // Auf Touch-Geräten ist die ganze Zeile 44 px hoch und antippbar, das Kästchen bleibt 24 px.
    <label className={cn('flex cursor-pointer items-start gap-2.5 touch:min-h-11 touch:items-center', rest.disabled && 'cursor-not-allowed opacity-45', className)}>
      <input
        ref={ref}
        type="checkbox"
        {...rest}
        className="mt-0.5 size-[18px] shrink-0 cursor-pointer rounded-xs accent-selection disabled:cursor-not-allowed touch:mt-0 touch:size-6"
      />
      {label && (
        <span className="flex-1">
          <span className="t-body-strong">{label}</span>
        </span>
      )}
    </label>
  );
});
